import React, { useState } from 'react';
import { Search, Eye, Edit, Check, X, Clock, Star } from 'lucide-react';

interface Template {
  id: number;
  name: string;
  category: string;
  image: string;
  popularity: string;
  customizable: boolean;
  description: string;
  features: string[];
  colors: string[];
  loadingSpeed: 'Fast' | 'Medium' | 'Slow';
  rating: number;
  reviews: number;
}

export const WebsiteTemplates: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedTemplate, setSelectedTemplate] = useState<number | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<number | null>(null);
  const [selectedColor, setSelectedColor] = useState<string | null>(null);

  const categories = [{
    id: 'all',
    name: '所有樣板'
  }, {
    id: 'modern',
    name: '現代風格'
  }, {
    id: 'classic',
    name: '經典風格'
  }, {
    id: 'rustic',
    name: '鄉村風格'
  }, {
    id: 'elegant',
    name: '優雅風格'
  }, {
    id: 'minimal',
    name: '極簡風格'
  }];

  const templates: Template[] = [{
    id: 1,
    name: '現代優雅',
    category: 'modern',
    image: 'https://images.unsplash.com/photo-1519741497674-611481863552?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2370&q=80',
    popularity: 'Popular',
    customizable: true,
    description: '簡潔精緻的設計，完美適合現代新人。具有流暢動畫和優雅字體。',
    features: ['自訂配色方案', '相片畫廊', 'RSVP 管理', '禮物清單', '互動時間軸'],
    colors: ['#2D3436', '#6C5CE7', '#00B894', '#FF7675'],
    loadingSpeed: 'Fast',
    rating: 4.8,
    reviews: 245
  }, {
    id: 2,
    name: '經典浪漫',
    category: 'classic',
    image: 'https://images.unsplash.com/photo-1469371670807-013ccf25f16a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2370&q=80',
    popularity: 'New',
    customizable: true,
    description: '永恆浪漫的設計，擁有美麗的字體和優雅細節。完美適合傳統儀式。',
    features: ['多種版面配置', '賓客留言簿', '婚禮派對介紹', '倒數計時器', '會場指引'],
    colors: ['#FDA7DF', '#D980FA', '#B53471', '#833471'],
    loadingSpeed: 'Medium',
    rating: 4.6,
    reviews: 189
  }, {
    id: 3,
    name: '鄉村魅力',
    category: 'rustic',
    image: 'https://images.unsplash.com/photo-1607190074257-dd4b7af0309f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2369&q=80',
    popularity: '',
    customizable: true,
    description: '溫馨迷人的設計，擁有自然紋理和大地色調。非常適合戶外和農場婚禮。',
    features: ['愛情故事時間軸', '相片牆', '數位邀請函', '天氣更新', '住宿資訊'],
    colors: ['#84817a', '#cc9966', '#996633', '#6b4423'],
    loadingSpeed: 'Fast',
    rating: 4.7,
    reviews: 156
  }, {
    id: 4,
    name: '優雅盛典',
    category: 'elegant',
    image: 'https://images.unsplash.com/photo-1465495976277-4387d4b0b4c6?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2370&q=80',
    popularity: 'Popular',
    customizable: true,
    description: '奢華精緻的設計，搭配金色點綴和頂級動畫效果。完美適合高檔慶典。',
    features: ['虛擬導覽', '菜單預覽', '服裝指南', '社群媒體整合', '私人相片分享'],
    colors: ['#2c3e50', '#34495e', '#7f8c8d', '#95a5a6'],
    loadingSpeed: 'Medium',
    rating: 4.9,
    reviews: 312
  }, {
    id: 5,
    name: '極簡時尚',
    category: 'minimal',
    image: 'https://images.unsplash.com/photo-1507504031003-b417219a0fde?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2370&q=80',
    popularity: 'New',
    customizable: false,
    description: '簡潔的極簡設計，專注於字體排版和留白空間。完美適合現代都市婚禮。',
    features: ['單頁式版面', '基本資訊', '行動優先設計', '快速載入', '簡易 RSVP'],
    colors: ['#000000', '#ffffff', '#cccccc', '#666666'],
    loadingSpeed: 'Fast',
    rating: 4.5,
    reviews: 98
  }, {
    id: 6,
    name: '花園派對',
    category: 'rustic',
    image: 'https://images.unsplash.com/photo-1519225421980-715cb0215aed?ixlib=rb-4.0.3&auto=format&fit=crop&w=2370&q=80',
    popularity: '',
    customizable: true,
    description: '清新活力的花卉設計，適合花園婚禮和春季慶典的完美選擇。',
    features: ['植物圖案設計', '活動時程表', '禮物清單', '賓客住宿', '當地景點'],
    colors: ['#00b894', '#55efc4', '#00cec9', '#81ecec'],
    loadingSpeed: 'Fast',
    rating: 4.7,
    reviews: 167
  }];

  const filteredTemplates = activeCategory === 'all' ? templates : templates.filter(template => template.category === activeCategory);
  const selectedTemplateData = templates.find(t => t.id === selectedTemplate);

  return <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <h2 className="text-xl font-semibold">婚禮網站樣板</h2>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <input type="text" placeholder="搜尋樣板..." className="pl-10 pr-4 py-2 w-full sm:w-64 rounded-md border border-border focus:outline-none focus:ring-2 focus:ring-primary/20" />
        </div>
      </div>

      <div className="flex overflow-x-auto pb-2 -mx-2 px-2">
        <div className="flex space-x-1">
          {categories.map(category => <button key={category.id} onClick={() => setActiveCategory(category.id)} className={`px-4 py-2 whitespace-nowrap rounded-md text-sm font-medium ${activeCategory === category.id ? 'bg-primary text-primary-foreground' : 'bg-secondary text-foreground hover:bg-secondary/80'}`}>
              {category.name}
            </button>)}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {filteredTemplates.map(template => <div key={template.id} className={`bg-white rounded-lg shadow overflow-hidden hover:shadow-md transition-shadow ${selectedTemplate === template.id ? 'ring-2 ring-primary' : ''}`} onClick={() => setSelectedTemplate(template.id)}>
                <div className="relative h-48 overflow-hidden group">
                  <img src={template.image} alt={template.name} className="w-full h-full object-cover transition-transform group-hover:scale-105" />
                  {template.popularity && <span className="absolute top-2 left-2 bg-primary text-primary-foreground text-xs px-2 py-1 rounded">
                      {template.popularity}
                    </span>}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center space-x-2">
                    <button className="p-2 bg-white rounded-full hover:bg-primary hover:text-primary-foreground" onClick={e => {
                  e.stopPropagation();
                  setPreviewTemplate(template.id);
                }}>
                      <Eye className="h-4 w-4" />
                    </button>
                    {template.customizable && <button className="p-2 bg-white rounded-full hover:bg-primary hover:text-primary-foreground" onClick={e => e.stopPropagation()}>
                        <Edit className="h-4 w-4" />
                      </button>}
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex justify-between items-center">
                    <h3 className="font-medium">{template.name}</h3>
                    <span className="text-xs capitalize px-2 py-1 bg-secondary rounded-full">
                      {template.category}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground line-clamp-2">
                    {template.description}
                  </p>
                  <div className="flex justify-between items-center mt-4">
                    <div className="flex items-center space-x-1">
                      <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                      <span className="text-sm font-medium">
                        {template.rating}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        ({template.reviews})
                      </span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">
                        {template.loadingSpeed}
                      </span>
                    </div>
                  </div>
                </div>
              </div>)}
          </div>
        </div>

        {selectedTemplateData && <div className="bg-white rounded-lg shadow p-6">
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold">
                  {selectedTemplateData.name}
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {selectedTemplateData.description}
                </p>
              </div>

              <div>
                <h4 className="font-medium mb-2">配色方案</h4>
                <div className="flex space-x-2">
                  {selectedTemplateData.colors.map((color, index) => <button key={index} onClick={() => setSelectedColor(color)} className={`w-8 h-8 rounded-full transition-transform hover:scale-110 ${selectedColor === color ? 'ring-2 ring-primary ring-offset-2' : ''}`} style={{
                backgroundColor: color
              }} />)}
                </div>
              </div>

              <div>
                <h4 className="font-medium mb-2">功能特色</h4>
                <ul className="space-y-2">
                  {selectedTemplateData.features.map((feature, index) => <li key={index} className="flex items-center text-sm">
                      <Check className="h-4 w-4 text-green-500 mr-2" />
                      {feature}
                    </li>)}
                </ul>
              </div>

              <button className="w-full py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90">
                使用此樣板
              </button>
            </div>
          </div>}
      </div>

      {previewTemplate && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-semibold">樣板預覽</h3>
              <button onClick={() => setPreviewTemplate(null)} className="p-1 hover:bg-secondary rounded-full">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <img src={templates.find(t => t.id === previewTemplate)?.image} alt="Template Preview" className="w-full rounded-lg shadow-lg" />
            </div>
          </div>
        </div>}
    </div>;
};